import React, { useState, useMemo, forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import { Card, Row, Col, Button, Form, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker from 'react-datepicker';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { useAdvanceTableContext } from 'providers/AdvanceTableProvider';
import FalconCardHeader from 'components/common/FalconCardHeader';

const columns = [
  { 
    accessorKey: 'cliente', 
    header: 'Cliente',
    cell: ({ row }) => (
      <div>
        <div className="fw-semibold text-800">{row.original.cliente}</div>
        <small className="text-600">ID: {row.original.id}</small>
      </div>
    )
  },
  { 
    accessorKey: 'businessUnit', 
    header: 'Business Unit',
    cell: ({ row }) => (
      <Badge bg="secondary" className="fs-11">
        {row.original.businessUnit}
      </Badge>
    )
  },
  { 
    accessorKey: 'descripcion', 
    header: 'Descripción',
    cell: ({ row }) => {
      const desc = row.original.descripcion || 'Sin descripción';
      const shortDesc = desc.length > 30 ? desc.substring(0, 30) + '...' : desc;
      
      return (
        <div title={desc} className="text-700">
          {shortDesc}
        </div>
      );
    }
  },
  { 
    accessorKey: 'fechasCompletas', 
    header: 'Fechas & Horas',
    cell: ({ row }) => (
      <div className="min-w-180">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <small className="text-600">Desde:</small>
          <div className="text-end">
            <div className="fw-semibold text-800 fs-11">{row.original.fechaDesde}</div>
            <small className="text-600">{row.original.horaDesde}</small>
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-600">Hasta:</small>
          <div className="text-end">
            <div className="fw-semibold text-800 fs-11">{row.original.fechaHasta}</div>
            <small className="text-600">{row.original.horaHasta}</small>
          </div>
        </div>
      </div>
    )
  },
  { 
    accessorKey: 'inversionesDetalle', 
    header: 'Inversiones & Moneda',
    cell: ({ row }) => {
      const invBruta = row.original.inversionBruta || 0;
      const invNeta = row.original.inversionNeta || 0;
      const asignado = row.original.asignado || 0;
      const moneda = row.original.moneda || 'USD';
      
      const inversionesTooltip = (
        <Tooltip>
          <div className="text-start">
            <div><strong>💰 Inversión Bruta:</strong> {moneda} ${invBruta.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
            <div><strong>💵 Inversión Neta:</strong> {moneda} ${invNeta.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
            <div><strong>📊 Asignado:</strong> {moneda} ${asignado.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
            <div><strong>🏦 Moneda:</strong> {moneda}</div>
          </div>
        </Tooltip>
      );
      
      return (
        <div className="min-w-150">
          <OverlayTrigger placement="top" overlay={inversionesTooltip}>
            <div className="cursor-pointer">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-600">Bruta:</small>
                <span className="fw-semibold text-600 fs-11">
                  {moneda} ${invBruta.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-700">Neta:</small>
                <span className="fw-semibold text-700 fs-11">
                  {moneda} ${invNeta.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-800">Asignado:</small>
                <span className="fw-semibold text-800 fs-11">
                  {moneda} ${asignado.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </OverlayTrigger>
        </div>
      );
    }
  },
  { 
    accessorKey: 'periodo', 
    header: 'Período & Progreso',
    cell: ({ row }) => {
      const progreso = row.original.progresoTemporal || 0;
      const diasRestantes = row.original.diasRestantes || 0;
      const duracionDias = row.original.duracionDias || 0;
      const diasTranscurridos = row.original.diasTranscurridos || 0;
      
      const progressTooltip = (
        <Tooltip>
          <div className="text-start">
            <div><strong>📅 Duración total:</strong> {duracionDias} días</div>
            <div><strong>✅ Días transcurridos:</strong> {diasTranscurridos}</div>
            <div><strong>⏳ Días restantes:</strong> {diasRestantes}</div>
            <div><strong>📊 Progreso temporal:</strong> {progreso.toFixed(1)}%</div>
          </div>
        </Tooltip>
      );
      
      return (
        <div className="min-w-150">
          <OverlayTrigger placement="top" overlay={progressTooltip}>
            <div className="cursor-pointer">
              <div className="progress mb-1" style={{ height: '6px' }}>
                <div 
                  className={`progress-bar ${progreso >= 100 ? 'bg-success' : 'bg-primary'}`}
                  style={{ width: `${Math.min(progreso, 100)}%` }}
                />
              </div>
              
              <div className="d-flex justify-content-between">
                <small className="text-600">{progreso.toFixed(1)}%</small>
                <small className="text-600">
                  {diasRestantes > 0 ? `${diasRestantes}d restantes` : 'Finalizada'}
                </small>
              </div>
            </div>
          </OverlayTrigger>
        </div>
      );
    }
  },
  { 
    accessorKey: 'presupuestoVsReal', 
    header: 'Presupuesto vs Real (Hoy)',
    cell: ({ row }) => {
      const gastado = row.original.gastado || 0;
      const deberiaGastar = row.original.deberiaGastar || 0;
      const porcentaje = deberiaGastar > 0 ? (gastado / deberiaGastar) * 100 : 0;
      const diferencia = gastado - deberiaGastar;
      
      // Determinar estado
      let statusColor, statusIcon;
      if (porcentaje > 110) {
        statusColor = 'danger';
        statusIcon = 'exclamation-triangle';
      } else if (porcentaje < 85) {
        statusColor = 'warning';
        statusIcon = 'clock';
      } else {
        statusColor = 'success';
        statusIcon = 'check-circle';
      }
      
      const budgetTooltip = (
        <Tooltip>
          <div className="text-start">
            <div><strong>🎯 Presupuesto esperado hasta hoy:</strong> ${deberiaGastar.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
            <div><strong>💰 Gasto real:</strong> ${gastado.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
            <div><strong>📈 Diferencia:</strong> <span className={diferencia >= 0 ? 'text-danger' : 'text-success'}>{diferencia >= 0 ? '+' : ''}${diferencia.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></div>
            <div><strong>📊 Ritmo de gasto:</strong> {porcentaje.toFixed(1)}% del esperado</div>
          </div>
        </Tooltip>
      );
      
      return (
        <div className="min-w-180">
          {/* Valores principales - VISIBLES CON 2 DECIMALES */}
          <div className="d-flex justify-content-between align-items-center mb-1">
            <small className="text-600">Esperado:</small>
            <span className="fw-semibold text-700 fs-11">
              ${deberiaGastar.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-600">Real:</small>
            <span className="fw-semibold text-800 fs-11">
              ${gastado.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          
          {/* Indicador visual + porcentaje */}
          <div className="d-flex justify-content-between align-items-center">
            <OverlayTrigger placement="top" overlay={budgetTooltip}>
              <Badge bg={statusColor} className="fs-11 cursor-pointer">
                <FontAwesomeIcon icon={statusIcon} className="me-1" />
                {porcentaje.toFixed(1)}%
              </Badge>
            </OverlayTrigger>
            
            <small className={`fw-semibold ${diferencia >= 0 ? 'text-danger' : 'text-success'}`}>
              {diferencia >= 0 ? '+' : ''}${Math.abs(diferencia).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </small>
          </div>
        </div>
      );
    }
  },
  { 
    accessorKey: 'realVsTotal', 
    header: 'Real vs Presupuesto Total',
    cell: ({ row }) => {
      const gastado = row.original.gastado || 0;
      const inversionNeta = row.original.inversionNeta || 0;
      const porcentajeTotal = inversionNeta > 0 ? (gastado / inversionNeta) * 100 : 0;
      const presupuestoRestante = Math.max(0, inversionNeta - gastado);
      
      // Determinar estado
      let statusColor, statusIcon;
      if (porcentajeTotal > 100) {
        statusColor = 'danger';
        statusIcon = 'exclamation-triangle';
      } else if (porcentajeTotal > 80) {
        statusColor = 'warning';
        statusIcon = 'clock';
      } else {
        statusColor = 'success';
        statusIcon = 'check-circle';
      }
      
      const totalBudgetTooltip = (
        <Tooltip>
          <div className="text-start">
            <div><strong>💵 Presupuesto total (Neto):</strong> ${inversionNeta.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
            <div><strong>💰 Gasto real acumulado:</strong> ${gastado.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
            <div><strong>💼 Presupuesto restante:</strong> ${presupuestoRestante.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
            <div><strong>📊 Porcentaje consumido:</strong> {porcentajeTotal.toFixed(1)}% del total</div>
            <div><strong>⚠️ Estado:</strong> {porcentajeTotal > 100 ? 'Sobrepasado' : porcentajeTotal > 80 ? 'Cerca del límite' : 'En rango normal'}</div>
          </div>
        </Tooltip>
      );
      
      return (
        <div className="min-w-180">
          {/* Valores principales - VISIBLES CON 2 DECIMALES */}
          <div className="d-flex justify-content-between align-items-center mb-1">
            <small className="text-600">Total:</small>
            <span className="fw-semibold text-700 fs-11">
              ${inversionNeta.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-600">Gastado:</small>
            <span className="fw-semibold text-800 fs-11">
              ${gastado.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
          </div>
          
          {/* Indicador visual + porcentaje */}
          <div className="d-flex justify-content-between align-items-center">
            <OverlayTrigger placement="top" overlay={totalBudgetTooltip}>
              <Badge bg={statusColor} className="fs-11 cursor-pointer">
                <FontAwesomeIcon icon={statusIcon} className="me-1" />
                {porcentajeTotal.toFixed(1)}%
              </Badge>
            </OverlayTrigger>
            
            <small className="text-success fw-semibold">
              ${presupuestoRestante.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </small>
          </div>
        </div>
      );
    }
  },
  { 
    accessorKey: 'performance', 
    header: 'Velocidad de Gasto',
    cell: ({ row }) => {
      const percentage = row.original.porcentajeOffset || 0;
      const velocidad = row.original.velocidadGasto || 0;
      const offset = row.original.inversionOffset || 0;
      const asignado = row.original.asignado || 0;
      
      let variant, icon, label;
      if (percentage > 110) {
        variant = 'danger';
        icon = 'arrow-up';
        label = 'Ritmo acelerado';
      } else if (percentage < 85) {
        variant = 'warning';
        icon = 'arrow-down';
        label = 'Ritmo lento';
      } else {
        variant = 'success';
        icon = 'check';
        label = 'Ritmo normal';
      }
      
      const performanceTooltip = (
        <Tooltip>
          <div className="text-start">
            <div><strong>🏆 Estado del ritmo:</strong> {label}</div>
            <div><strong>📊 Porcentaje de ejecución:</strong> {percentage.toFixed(2)}% del esperado</div>
            <div><strong>💸 Velocidad promedio:</strong> ${velocidad.toFixed(2)}/día</div>
            <div><strong>💰 Diferencia vs esperado:</strong> <span className={offset > 0 ? 'text-danger' : 'text-success'}>{offset > 0 ? '+' : ''}${offset.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
            <div><strong>💼 Presupuesto asignado:</strong> ${asignado.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          </div>
        </Tooltip>
      );
      
      return (
        <div className="text-center min-w-140">
          {/* Velocidad principal - VISIBLE */}
          <div className="fw-semibold text-800 fs-11 mb-1">
            ${velocidad.toFixed(0)}/día
          </div>
          
          {/* Estado del ritmo */}
          <OverlayTrigger placement="left" overlay={performanceTooltip}>
            <Badge bg={variant} className="fs-11 cursor-pointer mb-1">
          <FontAwesomeIcon icon={icon} className="me-1" />
              {percentage.toFixed(2)}%
        </Badge>
          </OverlayTrigger>
          
          {/* Diferencia vs esperado */}
          <div className={`small fw-semibold ${offset > 0 ? 'text-danger' : 'text-success'}`}>
            {offset > 0 ? '+' : ''}${Math.abs(offset).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      );
    }
  }
];
const DateRangeInput = forwardRef(({ value, onClick }, ref) => (
  <div className="position-relative">
    <Form.Control
      size="sm"
      ref={ref}
      onClick={onClick}
      value={value}
      className="ps-4"
      placeholder="Seleccionar rango de fechas"
      readOnly
    />
    <FontAwesomeIcon
      icon="calendar-alt"
      className="text-primary position-absolute top-50 translate-middle-y ms-2"
    />
  </div>
));

DateRangeInput.displayName = 'DateRangeInput';

const BudgetControlTable = ({ data, onApplyFilters, isLoading }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedClient, setSelectedClient] = useState('');
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  // Obtener lista única de clientes para el filtro
  const uniqueClients = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return [...new Set(data.map(item => item.cliente))].sort();
  }, [data]);

  // *** FUNCIÓN PARA APLICAR FILTROS AL BACKEND ***
  const applyFiltersToBackend = React.useCallback(async (filters = {}) => {
    if (!onApplyFilters || isApplyingFilters) return;
    
    setIsApplyingFilters(true);
    try {
      const backendFilters = {};
      
      // Convertir fechas a formato YYYY-MM-DD para backend
      if (filters.startDate) {
        backendFilters.dateFrom = filters.startDate.toISOString().split('T')[0];
      }
      if (filters.endDate) {
        backendFilters.dateTo = filters.endDate.toISOString().split('T')[0];
      }
      
      // Cliente (se puede implementar en backend después)
      if (filters.selectedClient) {
        // Por ahora se mantiene el filtro en frontend para cliente
        // backendFilters.clientName = filters.selectedClient;
      }
      
      // Solo hacer la llamada si hay filtros reales
      const hasFilters = Object.keys(backendFilters).length > 0;
      if (hasFilters) {

        await onApplyFilters(backendFilters);
      }
    } catch (error) {
      console.error('Error aplicando filtros:', error);
    } finally {
      setIsApplyingFilters(false);
    }
  }, [onApplyFilters, isApplyingFilters]);

  // *** EFECTO PARA APLICAR FILTROS CUANDO CAMBIEN (MEJORADO) ***
  React.useEffect(() => {
    // No aplicar filtros si ya se están aplicando o cargando
    if (isApplyingFilters || isLoading) return;
    
    // Solo aplicar filtros si AMBAS fechas están seleccionadas O si se limpiaron ambas
    const bothDatesSelected = startDate && endDate;
    const bothDatesCleared = !startDate && !endDate;
    
    if (!bothDatesSelected && !bothDatesCleared) {
      // Si solo hay una fecha seleccionada, no hacer nada
      return;
    }
    
    if (bothDatesSelected && startDate > endDate) {
      return;
    }
    
    const timeoutId = setTimeout(() => {
      applyFiltersToBackend({
        startDate,
        endDate,
        selectedClient
      });
    }, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [startDate, endDate, selectedClient, applyFiltersToBackend, isApplyingFilters, isLoading]);

  // Filtrar por cliente en frontend (por ahora)
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return [];
    }

    let filtered = [...data];

    // Filtrar por cliente en frontend
    if (selectedClient) {
      filtered = filtered.filter(item => 
        item.cliente.toLowerCase().includes(selectedClient.toLowerCase())
      );
    }

    return filtered;
  }, [data, selectedClient]);

  // *** TABLA CON DATOS FILTRADOS ***
  const table = useAdvanceTable({
    data: filteredData, // Solo filtro de cliente en frontend
    columns,
    sortable: true,
    pagination: true,
    selection: true,
    globalFilter: true
  });

  // *** FUNCIÓN PARA LIMPIAR FILTROS ***
  const clearFilters = async () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedClient('');
    setIsApplyingFilters(true);
    
    try {
      if (onApplyFilters) {
        await onApplyFilters({});
      }
    } catch (error) {
      // Error handling
    } finally {
      setIsApplyingFilters(false);
    }
  };

  // *** FUNCIÓN MEJORADA PARA MANEJAR CAMBIOS DE FECHA ***
  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    
    if (!start && !end) {
      applyFiltersToBackend({
        startDate: null,
        endDate: null,
        selectedClient
      });
    }
  };

  // *** FUNCIÓN DE EXPORTACIÓN ***
  const exportData = () => {
    if (!filteredData || filteredData.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    // Preparar datos para exportar
    const exportableData = filteredData.map(row => ({
      ID: row.id,
      Cliente: row.cliente,
      'Business Unit': row.businessUnit,
      Descripción: row.descripcion,
      'Fecha Desde': row.fechaDesde,
      'Hora Desde': row.horaDesde,
      'Fecha Hasta': row.fechaHasta,
      'Hora Hasta': row.horaHasta,
      Moneda: row.moneda,
      'Inversión Bruta': `${row.moneda} $${row.inversionBruta.toLocaleString()}`,
      'Inversión Neta': `${row.moneda} $${row.inversionNeta.toLocaleString()}`,
      'Asignado': `${row.moneda} $${row.asignado.toLocaleString()}`,
      'Presupuesto Esperado (Hoy)': `$${row.deberiaGastar.toLocaleString()}`,
      'Gasto Real': `$${row.gastado.toLocaleString()}`,
      'Diferencia vs Esperado': `$${row.inversionOffset.toLocaleString()}`,
      'Ritmo de Gasto': `${row.porcentajeOffset.toFixed(1)}%`,
      'Consumido del Total': `${row.inversionNeta > 0 ? ((row.gastado / row.inversionNeta) * 100).toFixed(1) : 0}%`,
      'Presupuesto Restante': `$${Math.max(0, row.inversionNeta - row.gastado).toLocaleString()}`,
      'Velocidad Diaria': `$${row.velocidadGasto.toFixed(2)}/día`,
      'Duración Días': row.duracionDias,
      'Días Transcurridos': row.diasTranscurridos,
      'Días Restantes': row.diasRestantes,
      'Progreso Temporal': `${row.progresoTemporal.toFixed(1)}%`,
      Status: row.status
    }));

    // Convertir a CSV
    const headers = Object.keys(exportableData[0]);
    const csvContent = [
      headers.join(','),
      ...exportableData.map(row => 
        headers.map(header => `"${row[header]}"`).join(',')
      )
    ].join('\n');

    // Descargar archivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `budget_control_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <AdvanceTableProvider {...table}>
      <BudgetControlTableContent 
        originalData={data}
        filteredData={filteredData}
        startDate={startDate}
        endDate={endDate}
        selectedClient={selectedClient}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        setSelectedClient={setSelectedClient}
        uniqueClients={uniqueClients}
        clearFilters={clearFilters}
        handleDateChange={handleDateChange}
        table={table}
        columns={columns}
        exportData={exportData}
        isApplyingFilters={isApplyingFilters}
        isLoading={isLoading}
        applyFiltersToBackend={applyFiltersToBackend}
      />
    </AdvanceTableProvider>
  );
};

const BudgetControlTableContent = ({ 
  originalData,
  filteredData,
  startDate, 
  endDate, 
  selectedClient,
  setStartDate,
  setEndDate, 
  setSelectedClient,
  uniqueClients,
  clearFilters: originalClearFilters,
  handleDateChange,
  table: tableFromProvider,
  columns,
  exportData,
  isApplyingFilters,
  isLoading,
  applyFiltersToBackend
}) => {
  const table = useAdvanceTableContext();
  const searchBoxRef = useRef(null);

  if (!originalData || !Array.isArray(originalData) || originalData.length === 0) {
    return (
      <Card className="h-100">
        <FalconCardHeader 
          title="Budget Control" 
          titleTag="h6"
          className="border-bottom"
        />
        <Card.Body className="text-center py-5">
          <FontAwesomeIcon icon="chart-line" className="fs-3 text-300 mb-3" />
          <h5 className="text-800">No hay datos disponibles</h5>
          <p className="text-600 mb-3">No se encontraron datos de budget control para mostrar.</p>
        </Card.Body>
      </Card>
    );
  }

  const clearFilters = () => {
    originalClearFilters();
    searchBoxRef.current?.reset();
  };

  // Obtener información de paginación de la tabla
  const { 
    getState, 
    getFilteredRowModel, 
    getPaginationRowModel 
  } = table;

  const state = getState ? getState() : {};
  const { pagination = { pageIndex: 0, pageSize: 10 } } = state;

  // Datos que se están mostrando actualmente (después de filtro de búsqueda y paginación)
  const currentPageData = getPaginationRowModel ? getPaginationRowModel().rows : [];
  const totalFilteredRecords = getFilteredRowModel ? getFilteredRowModel().rows.length : filteredData.length;

  // Calcular los registros mostrados en la página actual
  const startRecord = pagination.pageIndex * pagination.pageSize + 1;
  const endRecord = Math.min(startRecord + currentPageData.length - 1, totalFilteredRecords);

  return (
    <Card className="h-100">
      <FalconCardHeader 
        title={
          <div className="d-flex align-items-center">
            <span>{`Budget Control (${totalFilteredRecords} registros)`}</span>
            {(isApplyingFilters || isLoading) && (
              <div className="spinner-border spinner-border-sm ms-2 text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
            )}
          </div>
        }
        titleTag="h6"
        className="border-bottom"
      />
      
      {/* Filtros */}
      <Card.Body className="border-bottom bg-body-tertiary">
        <Row className="g-3 align-items-end">
          <Col lg={3} md={4} sm={6}>
            <Form.Label className="mb-1 fs-10 fw-semibold text-600">Rango de Fechas</Form.Label>
            <DatePicker
              selected={startDate}
              onChange={handleDateChange}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              dateFormat="dd/MM/yyyy"
              customInput={<DateRangeInput />}
              isClearable
              placeholderText="Seleccionar desde - hasta"
            />
            {/* Indicador de selección en progreso */}
            {startDate && !endDate && (
              <small className="text-warning d-block mt-1">
                <FontAwesomeIcon icon="clock" className="me-1" />
                Selecciona la fecha final del rango
              </small>
            )}
            {startDate && endDate && (
              <small className="text-success d-block mt-1">
                <FontAwesomeIcon icon="check" className="me-1" />
                Rango: {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
              </small>
            )}
          </Col>
          
          <Col lg={2} md={3} sm={6}>
            <Form.Label className="mb-1 fs-10 fw-semibold text-600">Cliente</Form.Label>
            <Form.Select
              size="sm"
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
            >
              <option value="">Todos los clientes</option>
              {uniqueClients.map(client => (
                <option key={client} value={client}>{client}</option>
              ))}
            </Form.Select>
          </Col>

          <Col lg={3} md={3} sm={6}>
            <Form.Label className="mb-1 fs-10 fw-semibold text-600">Buscar</Form.Label>
            <AdvanceTableSearchBox ref={searchBoxRef} placeholder="Buscar en la tabla..." />
          </Col>

          <Col lg={4} md={2} sm={12}>
            <div className="d-flex gap-2 flex-wrap">
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={clearFilters}
                disabled={isApplyingFilters || isLoading}
                className="d-flex align-items-center"
              >
                <FontAwesomeIcon icon="times" className="me-1" />
                Limpiar
              </Button>
              
              {/* Botón manual para aplicar filtros (opcional) */}
              {startDate && endDate && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => applyFiltersToBackend({ startDate, endDate, selectedClient })}
                  disabled={isApplyingFilters || isLoading}
                  className="d-flex align-items-center"
                >
                  {isApplyingFilters ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-1" role="status">
                        <span className="visually-hidden">Cargando...</span>
                      </div>
                      Aplicando...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon="filter" className="me-1" />
                      Aplicar Filtros
                    </>
                  )}
                </Button>
              )}
              
              <Button
                variant="outline-success"
                size="sm"
                onClick={exportData}
                disabled={isLoading || filteredData.length === 0}
                className="d-flex align-items-center"
              >
                <FontAwesomeIcon icon="download" className="me-1" />
                Exportar CSV
              </Button>
            </div>
          </Col>
        </Row>
      </Card.Body>

      {/* Tabla */}
      <Card.Body className="p-0">
        <AdvanceTable
          headerClassName="bg-body-tertiary text-nowrap align-middle"
          rowClassName="align-middle"
          tableProps={{
            className: 'fs-10 mb-0 overflow-hidden',
            size: 'lg',
            striped: true,
            hover: true
          }}
        />
      </Card.Body>

      {/* Paginación */}
      <Card.Footer className="bg-body-tertiary">
        <Row className="flex-between-center">
          <Col xs="auto">
            <small className="text-600">
              {totalFilteredRecords > 0 ? (
                <>Mostrando {startRecord} a {endRecord} de {totalFilteredRecords} registros</>
              ) : (
                <>No se encontraron registros</>
              )}
              {(startDate || endDate || selectedClient) && ' (filtrados)'}
            </small>
          </Col>
          <Col xs="auto">
            <AdvanceTablePagination />
          </Col>
        </Row>
      </Card.Footer>
    </Card>
  );
};

BudgetControlTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  onApplyFilters: PropTypes.func,
  isLoading: PropTypes.bool
};

BudgetControlTableContent.propTypes = {
  originalData: PropTypes.arrayOf(PropTypes.object),
  filteredData: PropTypes.arrayOf(PropTypes.object),
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  selectedClient: PropTypes.string,
  setStartDate: PropTypes.func,
  setEndDate: PropTypes.func,
  setSelectedClient: PropTypes.func,
  uniqueClients: PropTypes.arrayOf(PropTypes.string),
  clearFilters: PropTypes.func,
  handleDateChange: PropTypes.func,
  table: PropTypes.object,
  columns: PropTypes.array,
  exportData: PropTypes.func,
  isApplyingFilters: PropTypes.bool,
  isLoading: PropTypes.bool,
  applyFiltersToBackend: PropTypes.func
};

DateRangeInput.propTypes = {
  value: PropTypes.string,
  onClick: PropTypes.func
};

export default BudgetControlTable; 